// Global league — operator config for the public global leaderboard:
//   1. Ignore list — hide users from the board (autocomplete + Save).
//   2. Broker visibility — which brokers' users the board includes
//      (SM-774). ?broker=<id> highlights one broker's row; the broker
//      detail page links here with it.
//   3. Global ranking — the live board, profit / volume.
//
// The ignore list filters at ranking-service's ZSET-build time, so a Save
// takes effect within ~60s and is fully reversible. Private leagues are
// unaffected. Save success/failure surfaces as a toast (see the
// MutationCache in main.tsx) alongside the inline "Saved" hint.
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import {
  keys,
  fetchIgnoreList,
  updateIgnoreList,
  fetchUsersByIDs,
  fetchGlobalRanking,
} from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Pagination } from "@/components/ui/Pagination";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { UserMultiSelect, type PickedUser } from "@/components/leagues/UserMultiSelect";
import { BrokerVisibilityCard } from "@/components/leagues/BrokerVisibilityCard";
import { LeaderboardTable, type RankMetric } from "@/components/leagues/LeaderboardTable";
import { usePagination } from "@/lib/usePagination";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";

interface Search {
  broker?: string;
}

export const Route = createFileRoute("/_app/global-league")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    broker: typeof s.broker === "string" && s.broker.trim() ? s.broker.trim() : undefined,
  }),
  component: GlobalLeaguePage,
});

function GlobalLeaguePage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const perms = useMyPerms();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Global league</h1>
        <p className="text-sm text-muted">
          Hide users from the public global leaderboard, choose which brokers it includes, and review the live
          ranking.
        </p>
      </div>
      <IgnoreListCard />
      {perms.has(Perm.RankingView) ? (
        <BrokerVisibilityCard
          focusBroker={search.broker}
          onClearFocus={() => void navigate({ search: {} })}
        />
      ) : null}
      <GlobalRankingCard />
    </div>
  );
}

function IgnoreListCard() {
  const qc = useQueryClient();
  const perms = useMyPerms();
  const canEdit = perms.has(Perm.RankingConfig);

  const ignore = useQuery({ queryKey: keys.ignoreList(), queryFn: fetchIgnoreList });
  const ids = useMemo(() => (ignore.data ?? []).map((e) => e.user_id), [ignore.data]);

  // Resolve the current ids → profiles so the chips show names, not raw ids.
  const profiles = useQuery({
    queryKey: [...keys.users, "by-ids", ids] as const,
    queryFn: () => fetchUsersByIDs(ids),
    enabled: ids.length > 0,
  });
  const profileMap = useMemo(() => {
    const m = new Map<string, PickedUser>();
    for (const u of profiles.data ?? []) {
      m.set(u.user_id, {
        user_id: u.user_id,
        display_name: u.display_name,
        email: u.email,
        avatar_url: u.avatar_url,
      });
    }
    return m;
  }, [profiles.data]);

  // Working selection — seeded once from the loaded list, then edited locally
  // until Save. New picks already carry their names (from the search results);
  // pre-existing ids get names merged in from profileMap at render.
  const [selected, setSelected] = useState<PickedUser[] | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (selected === null && ignore.data) {
      setSelected(ignore.data.map((e) => ({ user_id: e.user_id })));
    }
  }, [ignore.data, selected]);

  const displaySelected = useMemo(
    () =>
      (selected ?? []).map((u) =>
        u.display_name || u.email ? u : { ...u, ...(profileMap.get(u.user_id) ?? {}) },
      ),
    [selected, profileMap],
  );

  const dirty = useMemo(() => {
    if (!selected || !ignore.data) return false;
    const a = new Set(selected.map((u) => u.user_id));
    if (a.size !== ignore.data.length) return true;
    return ignore.data.some((e) => !a.has(e.user_id));
  }, [selected, ignore.data]);

  const save = useMutation({
    mutationFn: () => updateIgnoreList((selected ?? []).map((u) => u.user_id)),
    onSuccess: async (items) => {
      setSaved(true);
      setSelected(items.map((e) => ({ user_id: e.user_id })));
      await qc.invalidateQueries({ queryKey: keys.ranking });
    },
    meta: { successMessage: "Ignore list saved." },
  });

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Leaderboard ignore list</CardTitle>
        <span className="text-2xs text-muted">{displaySelected.length} hidden</span>
      </CardHeader>
      <CardBody className="space-y-4 pt-5">
        <p className="text-xs text-muted">
          Users on this list are hidden from the public global leaderboard, including their own rank.
          Removing a user restores them. Changes take effect within ~60 seconds. Private leagues are unaffected.
        </p>
        {ignore.isLoading || selected === null ? (
          <Skeleton className="h-24" />
        ) : (
          <UserMultiSelect
            selected={displaySelected}
            onChange={(next) => {
              setSelected(next);
              setSaved(false);
            }}
            disabled={!canEdit}
          />
        )}
        {save.isError && (
          <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger">
            {(save.error as Error).message}
          </p>
        )}
        {saved && !dirty && !save.isPending && (
          <p className="rounded bg-success/10 px-3 py-2 text-xs text-success">Ignore list saved.</p>
        )}
        <div className="flex items-center gap-3">
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canEdit || !dirty}>
            Save
          </Button>
          {!canEdit && (
            <span className="text-2xs text-muted">You need the RANKING_CONFIG permission to edit this list.</span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

function GlobalRankingCard() {
  const [metric, setMetric] = useState<RankMetric>("profit");
  const { page, setPage, limit, offset } = usePagination();

  // Switching metric resets to the first page (the boards differ).
  useEffect(() => {
    setPage(0);
  }, [metric]); // eslint-disable-line react-hooks/exhaustive-deps

  const board = useQuery({
    queryKey: keys.globalRank(metric, limit, offset),
    queryFn: () => fetchGlobalRanking({ metric, limit, offset }),
  });

  return (
    <Card>
      <CardHeader className="border-b border-border">
        <CardTitle>Global ranking</CardTitle>
        <SegmentedControl<RankMetric>
          options={[
            { value: "profit", label: "Profit" },
            { value: "volume", label: "Volume" },
          ]}
          value={metric}
          onChange={setMetric}
        />
      </CardHeader>
      <CardBody className="p-0">
        {board.isLoading && (
          <div className="space-y-2 p-5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        )}
        {board.data && board.data.items.length === 0 && (
          <p className="px-5 py-16 text-center text-sm text-muted">The leaderboard is empty.</p>
        )}
        {board.data && board.data.items.length > 0 && (
          <LeaderboardTable rows={board.data.items} metric={metric} />
        )}
        {board.data && (
          <Pagination
            page={board.data.page}
            currentPage={page}
            onPageChange={setPage}
            itemLabel="users"
          />
        )}
      </CardBody>
    </Card>
  );
}
